/* eslint-disable */
import React, { useState, useEffect } from "react";
import {
  Box, Text, Flex, Heading, Button,
  Modal, ModalOverlay, ModalContent, VStack, Grid, Spinner, useDisclosure, Icon,
  Checkbox, CheckboxGroup, useToast, Progress, useColorModeValue, useColorMode
} from "@chakra-ui/react";
import { 
  FaBrain, FaPlay, FaLayerGroup 
} from "react-icons/fa";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import CourseComponent from "../components/UserComponents/CourseComponent";
import HeroBannerGSAP from "../components/UserComponents/HeroBannerGSAP";
import LiveSessionsWidget from "../components/UserComponents/LiveSessionsWidget";
import LnaMonthlyModal from "../components/UserComponents/LnaMonthlyModal";

/* ═══════════════════════════════════════════════
   DYNAMIC PASTEL PALETTE — Light/Dark Mode Safe
═══════════════════════════════════════════════ */
const getStatusBadge = (status, isOverdue) => {
  if (isOverdue)              return { label: "Overdue 🚨",    lightBg: "#E53E3E", darkBg: "#E53E3E" };
  if (status === "Completed") return { label: "Completed ✅",  lightBg: "#38A169", darkBg: "#38A169" };
  if (status === "In Progress") return { label: "In Progress ⚡", lightBg: "#D69E2E", darkBg: "#D69E2E" };
  return                             { label: "Not Started",   lightBg: "#718096", darkBg: "#718096" };
};

const UserDashboard = () => {
  const [lastActive, setLastActive]         = useState(null);
  const [enrollments, setEnrollments]       = useState([]);
  const [isLoading, setIsLoading]           = useState(false);
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose }         = useDisclosure();
  const [selectedBundle, setSelectedBundle] = useState(null);
  const { isOpen: isLnaOpen, onOpen: onLnaOpen, onClose: onLnaClose } = useDisclosure();
  const [lnaStruggles, setLnaStruggles]     = useState([]);
  const [isSubmittingLna, setIsSubmittingLna] = useState(false);
  const [lnaAnalysis, setLnaAnalysis] = useState(null);
  const [showMonthlyLna, setShowMonthlyLna] = useState(false);
  const toast = useToast();

  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";

  const textDark = useColorModeValue("#0a2628", "var(--text-main, #EDE9FF)");
  const textMuted = useColorModeValue("#7a9c9f", "var(--text-muted, #9B98BC)");
  const cardBg = useColorModeValue("white", "var(--bg-surface, #161428)");
  const borderColor = useColorModeValue("#eef2f0", "var(--border-soft, rgba(255, 255, 255, 0.08))");
  const iconBg = useColorModeValue("rgba(0,0,0,0.05)", "rgba(255,255,255,0.05)");
  const btnColor = useColorModeValue("white", "var(--bg-page, #0D0B1A)");

  const PALETTE = [
    { bg: useColorModeValue("#c3f0c8", "#2a3d1b"), text: useColorModeValue("#0a2628", "#c3f0c8") }, // Mint
    { bg: useColorModeValue("#ffb99c", "#4a2410"), text: useColorModeValue("#0a2628", "#ffb99c") }, // Orange
    { bg: useColorModeValue("#dfc4fd", "#2c1c4d"), text: useColorModeValue("#0a2628", "#dfc4fd") }, // Lilac
    { bg: useColorModeValue("#b4daff", "#18324f"), text: useColorModeValue("#0a2628", "#b4daff") }, // Sky Blue
    { bg: useColorModeValue("#ffeb8d", "#4d410d"), text: useColorModeValue("#0a2628", "#ffeb8d") }, // Yellow
  ];

  useEffect(() => {
    fetchLastActive();
    fetchMyEnrollments();
    fetchLnaAnalysis();
    const u = JSON.parse(localStorage.getItem("user"))?.user;
    const role = u?.role || JSON.parse(localStorage.getItem("user"))?.role;
    if (u && u.hasCompletedLna === false && role === 'employee') onLnaOpen();
  }, []);

  const fetchLnaAnalysis = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.token || "";
      if (!token) return;
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/lna/my-analysis`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLnaAnalysis(res.data);
      
      const role = JSON.parse(localStorage.getItem("user"))?.user?.role || JSON.parse(localStorage.getItem("user"))?.role;
      if (res.data.needsMonthlySurvey && role === 'employee') {
        setShowMonthlyLna(true);
      } else {
        setShowMonthlyLna(false);
      }
    } catch (err) {
      console.error("Failed to fetch LNA analysis", err);
    }
  };

  const handleLnaSubmit = async () => {
    if (!lnaStruggles.length) { toast({ title: "Please select at least one area", status: "warning" }); return; }
    setIsSubmittingLna(true);
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.token || "";
      await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/lna-survey`,
        { struggles: lnaStruggles },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const s = JSON.parse(localStorage.getItem("user"));
      if (s?.user) { s.user.hasCompletedLna = true; localStorage.setItem("user", JSON.stringify(s)); }
      toast({ title: "Survey completed!", description: "We'll personalise your training.", status: "success" });
      onLnaClose();
    } catch { toast({ title: "Error submitting survey", status: "error" }); }
    finally { setIsSubmittingLna(false); }
  };

  const fetchMyEnrollments = async () => {
    setIsLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.token || "";
      if (!token) return;
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/enrollments/my-courses`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEnrollments(res.data);
    } catch (err) { console.error(err); }
    finally { setIsLoading(false); }
  };

  const fetchLastActive = async () => {
    try {
      const token = JSON.parse(localStorage.getItem("user"))?.token || "";
      if (!token) return;
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/enrollments/last-active`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLastActive(res.data);
    } catch (err) { console.error(err); }
  };

  const userData = JSON.parse(localStorage.getItem("user"))?.user;

  const buildGrouped = () => {
    const grouped = [], bMap = {};
    enrollments.filter(i => i.courseId).forEach(item => {
      if (item.bundleId?._id) {
        if (!bMap[item.bundleId._id]) {
          bMap[item.bundleId._id] = { isBundle: true, bundleObj: item.bundleId, courses: [], overallProgress: 0, totalCourses: 0, completedCourses: 0, isOverdue: false, dueDate: item.dueDate };
          grouped.push(bMap[item.bundleId._id]);
        }
        const g = bMap[item.bundleId._id];
        g.courses.push(item); g.totalCourses++;
        if (item.status === "Completed") g.completedCourses++;
        if (item.dueDate && new Date() > new Date(item.dueDate) && item.status !== "Completed") g.isOverdue = true;
        g.overallProgress = Math.round(g.courses.reduce((a, c) => a + (c.progress || 0), 0) / g.totalCourses);
      } else {
        grouped.push({ isBundle: false, item });
      }
    });
    return grouped;
  };

  // KPIs
  const totalCourses = enrollments.length;
  const completedCourses = enrollments.filter(e => e.status === "Completed").length;
  const inProgress = totalCourses - completedCourses;
  const completionRate = totalCourses > 0 ? Math.round((completedCourses / totalCourses) * 100) : 0;

  const KPICard = ({ bg, title, value, subtext }) => (
    <Box bg={bg} p={5} borderRadius="3xl" position="relative" overflow="hidden" boxShadow="0 4px 15px rgba(0,0,0,0.02)">
      <Text fontSize="xs" fontWeight="800" color={isDark ? "white" : "#0a2628"} opacity={0.7} mb={1}>{title}</Text>
      <Heading size="lg" color={isDark ? "white" : "#0a2628"} letterSpacing="-0.5px" mb={1}>{value}</Heading>
      <Text fontSize="10px" fontWeight="800" color={isDark ? "white" : "#0a2628"} opacity={0.6}>{subtext}</Text>
    </Box>
  );

  const allGroups = buildGrouped();
  const activeTasks = allGroups.filter(g => g.isBundle ? g.overallProgress < 100 : g.item.status !== "Completed");
  const completedTasks = allGroups.filter(g => g.isBundle ? g.overallProgress === 100 : g.item.status === "Completed");

  return (
    <Box id="dashboard-top">
      {/* Hero Banner */}
      <Box mb={8}>
        <HeroBannerGSAP userData={userData} />
      </Box>

      <Flex justify="space-between" align="end" mb={6}>
        <Box>
          <Heading size="lg" color={textDark} fontWeight="900" letterSpacing="-0.5px">Welcome {userData?.fullName?.split(" ")[0]}!</Heading>
          <Text fontSize="sm" color={textMuted} fontWeight="600" mt={1}>Here is your learning summary.</Text>
        </Box>
      </Flex>

      {/* Personal KPIs */}
      <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", xl: "repeat(4, 1fr)" }} gap={5} mb={8}>
        <KPICard bg={PALETTE[0].bg} title="Total Enrolled" value={totalCourses} subtext="Courses on your roster" />
        <KPICard bg={PALETTE[1].bg} title="In Progress" value={inProgress} subtext="Keep going!" />
        <KPICard bg={PALETTE[3].bg} title="Completed" value={completedCourses} subtext="Great job!" />
        <KPICard bg={PALETTE[2].bg} title="Completion Rate" value={completionRate + "%"} subtext="Overall progress" />
      </Grid>

      {/* Continue Learning Strip */}
      {lastActive?.courseId && (
        <Box mb={8} bg={cardBg} p={6} borderRadius="3xl" boxShadow="0 4px 20px rgba(0,0,0,0.02)">
          <Flex direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "flex-start", md: "center" }} gap={5}>
            <Flex align="center" gap={4} flex={1}>
              <Box w="80px" h="80px" bg={iconBg} borderRadius="2xl" display="flex" alignItems="center" justifyContent="center">
                <Icon as={FaPlay} color={textDark} boxSize={6} opacity={0.6} />
              </Box>
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Box w={2} h={2} bg={PALETTE[2].bg} borderRadius="full" />
                  <Text fontSize="10px" fontWeight="800" color={textMuted} letterSpacing="0.5px">CONTINUE WHERE YOU LEFT OFF</Text>
                </Flex>
                <Heading size="md" fontWeight="900" color={textDark} letterSpacing="-0.5px">
                  {lastActive.courseId.title}
                </Heading>
                <Text fontSize="xs" color={textMuted} fontWeight="600" mt={1}>
                  {lastActive.courseId.category || "General"}
                </Text>
              </Box>
            </Flex>

            <Flex direction="column" gap={3} align={{ base: "stretch", md: "flex-end" }} w={{ base: "full", md: "250px" }}>
              <Flex justify="space-between" fontSize="11px" fontWeight="800" color={textDark} w="full">
                <Text>Progress</Text><Text>{lastActive.progress || 0}%</Text>
              </Flex>
              <Progress value={lastActive.progress || 0} size="sm" w="full" colorScheme="teal" bg={iconBg} borderRadius="full" />
              <Button
                bg={textDark} color={btnColor} h="40px" borderRadius="full" fontWeight="800" fontSize="sm"
                onClick={() => navigate(`/course/${lastActive.courseId._id}`)}
                _hover={{ opacity: 0.9, transform: "translateY(-2px)" }}
                transition="all 0.2s" mt={2}
              >
                Resume Course →
              </Button>
            </Flex>
          </Flex>
        </Box>
      )}

      {/* Assigned Tasks & Targets */}
      <Box mb={8} id="my-courses">
        <Flex align="center" justify="space-between" mb={5}>
          <Heading size="md" fontWeight="900" color={textDark} letterSpacing="-0.5px">
            Assigned Tasks & Targets
          </Heading>
          <Button size="sm" variant="ghost" color={textDark} fontWeight="800" fontSize="xs">View All</Button>
        </Flex>

        {isLoading ? (
          <Flex justify="center" py={10}><Spinner size="lg" color={textDark} thickness="3px" /></Flex>
        ) : enrollments.filter(i => i.courseId).length === 0 ? (
          <Box bg={cardBg} borderRadius="3xl" p={10} textAlign="center">
            <Text fontSize="3xl" mb={3}>📚</Text>
            <Text color={textMuted} fontSize="sm" fontWeight="600">No courses currently assigned.</Text>
          </Box>
        ) : (
          <Grid templateColumns={{ base: "1fr", md: "repeat(2,1fr)", lg: "repeat(3,1fr)" }} gap={5}>
            {activeTasks.map((group, idx) => {
              const pal = PALETTE[idx % PALETTE.length];
              
              if (group.isBundle) {
                const { bundleObj, overallProgress, totalCourses, completedCourses, isOverdue } = group;
                const stBadge = getStatusBadge(overallProgress === 100 ? "Completed" : overallProgress > 0 ? "In Progress" : "Not Started", isOverdue);
                return (
                  <Box key={idx} bg={pal.bg} p={6} borderRadius="3xl" transition="all 0.2s" _hover={{ transform: "translateY(-4px)" }}>
                    <Flex justify="space-between" align="center" mb={4}>
                      <Box bg={cardBg} px={3} py={1} borderRadius="full">
                        <Text fontSize="9px" fontWeight="800" color={textDark}><Icon as={FaLayerGroup} mr={1}/> Bundle</Text>
                      </Box>
                      <Box bg={isDark ? stBadge.darkBg : stBadge.lightBg} px={3} py={1} borderRadius="full">
                        <Text fontSize="9px" fontWeight="800" color="white">{stBadge.label}</Text>
                      </Box>
                    </Flex>
                    <Heading size="sm" fontWeight="900" color={pal.text} mb={4} lineHeight="1.4" minH="40px">
                      {bundleObj.title || "Corporate Bundle"}
                    </Heading>
                    <Flex justify="space-between" fontSize="10px" fontWeight="800" color={pal.text} mb={2} opacity={0.8}>
                      <Text>{completedCourses}/{totalCourses} courses</Text>
                      <Text>{overallProgress}%</Text>
                    </Flex>
                    <Progress value={overallProgress} size="xs" bg="rgba(0,0,0,0.06)" colorScheme="blackAlpha" borderRadius="full" mb={5} />
                    <Button 
                      w="full" size="sm" bg={cardBg} color={pal.text} borderRadius="full" fontWeight="800"
                      onClick={() => { setSelectedBundle(group); onOpen(); }}
                    >
                      Open Bundle →
                    </Button>
                  </Box>
                );
              } else {
                const { item } = group;
                const isOverdue = item.dueDate && new Date() > new Date(item.dueDate) && item.status !== "Completed";
                const stBadge = getStatusBadge(item.status, isOverdue);
                const courseObj = item.courseId || {};
                return (
                  <Box key={idx} bg={pal.bg} p={6} borderRadius="3xl" transition="all 0.2s" _hover={{ transform: "translateY(-4px)" }}>
                    <Flex justify="space-between" align="center" mb={4}>
                      <Box bg={cardBg} px={3} py={1} borderRadius="full">
                        <Text fontSize="9px" fontWeight="800" color={textDark}>{courseObj.category || "General"}</Text>
                      </Box>
                      <Box bg={isDark ? stBadge.darkBg : stBadge.lightBg} px={3} py={1} borderRadius="full">
                        <Text fontSize="9px" fontWeight="800" color="white">{stBadge.label}</Text>
                      </Box>
                    </Flex>
                    <Heading size="sm" fontWeight="900" color={pal.text} mb={4} lineHeight="1.4" minH="40px">
                      {courseObj.title || "Untitled Course"}
                    </Heading>
                    <Flex justify="space-between" fontSize="10px" fontWeight="800" color={pal.text} mb={2} opacity={0.8}>
                      <Text>Progress</Text>
                      <Text>{item.progress || 0}%</Text>
                    </Flex>
                    <Progress value={item.progress || 0} size="xs" bg="rgba(0,0,0,0.06)" colorScheme="blackAlpha" borderRadius="full" mb={5} />
                    <Button 
                      w="full" size="sm" bg={item.status === "Completed" ? "transparent" : cardBg} 
                      color={pal.text} border={item.status === "Completed" ? "2px solid" : "none"} borderColor={cardBg}
                      borderRadius="full" fontWeight="800"
                      onClick={() => navigate(`/course/${courseObj._id || item.courseId}`)}
                    >
                      {item.status === "Completed" ? "Review Course" : "Start Course →"}
                    </Button>
                  </Box>
                );
              }
            })}
          </Grid>
        )}
      </Box>

      {/* Completed Courses & Certificates */}
      {completedTasks.length > 0 && (
        <Box mb={8} id="completed-courses">
          <Flex align="center" justify="space-between" mb={5}>
            <Heading size="md" fontWeight="900" color={textDark} letterSpacing="-0.5px">
              Completed Courses & Certificates
            </Heading>
          </Flex>
          <Grid templateColumns={{ base: "1fr", md: "repeat(2,1fr)", lg: "repeat(3,1fr)" }} gap={5}>
            {completedTasks.map((group, idx) => {
              const pal = PALETTE[(idx + activeTasks.length) % PALETTE.length];
              
              if (group.isBundle) {
                const { bundleObj, totalCourses } = group;
                return (
                  <Box key={`comp-b-${idx}`} bg={pal.bg} p={6} borderRadius="3xl" transition="all 0.2s" _hover={{ transform: "translateY(-4px)" }}>
                    <Flex justify="space-between" align="center" mb={4}>
                      <Box bg={cardBg} px={3} py={1} borderRadius="full">
                        <Text fontSize="9px" fontWeight="800" color={textDark}><Icon as={FaLayerGroup} mr={1}/> Bundle</Text>
                      </Box>
                      <Box bg={isDark ? "#38A169" : "#38A169"} px={3} py={1} borderRadius="full">
                        <Text fontSize="9px" fontWeight="800" color="white">Completed ✅</Text>
                      </Box>
                    </Flex>
                    <Heading size="sm" fontWeight="900" color={pal.text} mb={4} lineHeight="1.4" minH="40px">
                      {bundleObj.title || "Corporate Bundle"}
                    </Heading>
                    <Text fontSize="10px" fontWeight="800" color={pal.text} mb={5} opacity={0.8}>{totalCourses} courses finished</Text>
                    <Button 
                      w="full" size="sm" bg="transparent" border="2px solid" borderColor={cardBg} color={pal.text} borderRadius="full" fontWeight="800"
                      onClick={() => { setSelectedBundle(group); onOpen(); }}
                    >
                      View Certificates
                    </Button>
                  </Box>
                );
              } else {
                const { item } = group;
                const courseObj = item.courseId || {};
                return (
                  <Box key={`comp-c-${idx}`} bg={pal.bg} p={6} borderRadius="3xl" transition="all 0.2s" _hover={{ transform: "translateY(-4px)" }}>
                    <Flex justify="space-between" align="center" mb={4}>
                      <Box bg={cardBg} px={3} py={1} borderRadius="full">
                        <Text fontSize="9px" fontWeight="800" color={textDark}>{courseObj.category || "General"}</Text>
                      </Box>
                      <Box bg={isDark ? "#3a5c1a" : "#e7f9d5"} px={3} py={1} borderRadius="full">
                        <Text fontSize="9px" fontWeight="800" color={isDark ? "white" : textDark}>Completed ✅</Text>
                      </Box>
                    </Flex>
                    <Heading size="sm" fontWeight="900" color={pal.text} mb={4} lineHeight="1.4" minH="40px">
                      {courseObj.title || "Untitled Course"}
                    </Heading>
                    <Text fontSize="10px" fontWeight="800" color={pal.text} mb={5} opacity={0.8}>Finished</Text>
                    <Button 
                      w="full" size="sm" bg="transparent" border="2px solid" borderColor={cardBg} color={pal.text} borderRadius="full" fontWeight="800"
                      onClick={() => {
                        const token = JSON.parse(localStorage.getItem("user"))?.token || "";
                        window.open(`${process.env.REACT_APP_API_URL}/api/certificates/${item._id}?token=${token}`, '_blank');
                      }}
                    >
                      View Certificate
                    </Button>
                  </Box>
                );
              }
            })}
          </Grid>
        </Box>
      )}

      {/* MY LEARNING NEEDS SECTION */}
      {lnaAnalysis && lnaAnalysis.lnaScores && lnaAnalysis.lnaScores.some(s => s.score > 60) && (
        <Box mt={10} mb={6}>
          <Flex align="center" gap={3} mb={6}>
            <Box p={3} bg="purple.50" borderRadius="2xl" color="purple.600">
              <FaBrain size={24} />
            </Box>
            <Box>
              <Heading size="lg" fontWeight="900" color={textDark}>My Learning Needs</Heading>
              <Text fontSize="sm" color={textMuted} fontWeight="600">Based on your recent performance, surveys, and time-in-course</Text>
            </Box>
          </Flex>

          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6}>
            {lnaAnalysis.lnaScores.filter(s => s.score > 60).sort((a,b) => b.score - a.score).map((scoreObj, idx) => {
              let improvementLabel = null;
              if (scoreObj.history && scoreObj.history.length > 1) {
                  const lastScore = scoreObj.history[scoreObj.history.length - 2].score;
                  const currentScore = scoreObj.score;
                  const diff = lastScore - currentScore; // if diff > 0, difficulty dropped (improved!)
                  if (diff > 0) improvementLabel = <Text fontSize="xs" fontWeight="800" color="green.500" bg="green.50" px={2} py={1} borderRadius="md">📉 -{diff} (Improving)</Text>;
                  else if (diff < 0) improvementLabel = <Text fontSize="xs" fontWeight="800" color="red.500" bg="red.50" px={2} py={1} borderRadius="md">📈 +{Math.abs(diff)} (Worsening)</Text>;
              }

              return (
              <Box key={idx} p={5} bg={cardBg} borderRadius="3xl" boxShadow="sm" border="1px solid" borderColor={iconBg}>
                <Flex justify="space-between" align="center" mb={3}>
                  <Heading size="md" fontWeight="800" color={textDark}>{scoreObj.category}</Heading>
                  <Flex gap={2} align="center">
                    {improvementLabel}
                    <Text fontSize="xs" fontWeight="800" color="red.500" bg="red.50" px={3} py={1} borderRadius="full">Difficulty: {scoreObj.score}/100</Text>
                  </Flex>
                </Flex>
                <Progress value={scoreObj.score} colorScheme="red" size="xs" borderRadius="full" mb={2} />
                
                {scoreObj.breakdown && (
                  <Flex gap={2} mb={4} fontSize="10px" fontWeight="700" color={textMuted} wrap="wrap">
                    <Text>Score Breakdown:</Text>
                    <Text color="purple.500">Feedback: {scoreObj.breakdown.surveyPercent}%</Text>
                    <Text>|</Text>
                    <Text color="blue.500">Watch Time: {scoreObj.breakdown.timePercent}%</Text>
                    <Text>|</Text>
                    <Text color="orange.500">Check-ins: {scoreObj.breakdown.quizPercent}%</Text>
                  </Flex>
                )}

                {lnaAnalysis.suggestedCourses && lnaAnalysis.suggestedCourses.filter(c => c.category === scoreObj.category).length > 0 ? (
                  <Box mt={4}>
                    <Text fontSize="xs" fontWeight="700" color={textMuted} mb={2} textTransform="uppercase">Recommended Gap-Filler Courses:</Text>
                    {lnaAnalysis.suggestedCourses.filter(c => c.category === scoreObj.category).map(course => (
                      <Flex key={course._id} align="center" justify="space-between" p={3} bg={iconBg} borderRadius="xl" mb={2}>
                        <Box>
                          <Text fontSize="sm" fontWeight="700" color={textDark} noOfLines={1}>{course.title}</Text>
                          <Text fontSize="10px" fontWeight="600" color={textMuted}>{course.level} Level</Text>
                        </Box>
                        <Button size="xs" colorScheme="purple" borderRadius="full" onClick={() => navigate(`/course/${course._id}`)}>Start</Button>
                      </Flex>
                    ))}
                  </Box>
                ) : (
                  <Text fontSize="xs" fontStyle="italic" color={textMuted}>No additional courses available in this category yet.</Text>
                )}
              </Box>
            )})}
          </Grid>
        </Box>
      )}

      <CourseComponent />
      <Box mt={8} id="live-sessions">
        <LiveSessionsWidget />
      </Box>

      {/* LNA Survey Modal */}
      <Modal isOpen={isLnaOpen} onClose={() => {}} isCentered closeOnOverlayClick={false}>
        <ModalOverlay backdropFilter="blur(4px)" bg="rgba(10,38,40,0.4)" />
        <ModalContent borderRadius="3xl" overflow="hidden" p={6} bg={cardBg} boxShadow="0 24px 60px rgba(0,0,0,0.1)">
          <Flex align="center" gap={3} mb={6}>
            <Box p={3} bg={PALETTE[1].bg} borderRadius="2xl" color={textDark}>
              <FaBrain size={24} />
            </Box>
            <Box>
              <Heading size="md" fontWeight="900" color={textDark}>Welcome to SkillUp!</Heading>
              <Text fontSize="sm" color={textMuted} fontWeight="600">Personalise your training.</Text>
            </Box>
          </Flex>
          <Text fontWeight="800" fontSize="sm" color={textDark} mb={4}>Which areas do you want to improve?</Text>
          <CheckboxGroup colorScheme="teal" onChange={setLnaStruggles} value={lnaStruggles}>
            <VStack align="stretch" spacing={3} mb={6}>
              {["Conversational & Soft Skills", "Technical Implementation", "Leadership & Management", "Compliance & Ethics"].map((label, idx) => (
                <Box key={idx} bg={isDark ? "var(--bg-raised, #1E1B35)" : "#f4fbf9"} p={4} borderRadius="2xl">
                  <Checkbox value={label} fontWeight="700" color={textDark}>{label}</Checkbox>
                </Box>
              ))}
            </VStack>
          </CheckboxGroup>
          <Button
            w="full" h="50px" bg={textDark} color={btnColor} borderRadius="full" fontWeight="800"
            onClick={handleLnaSubmit} isLoading={isSubmittingLna} _hover={{ opacity: 0.9 }}
          >
            Continue to Dashboard →
          </Button>
        </ModalContent>
      </Modal>

      <LnaMonthlyModal 
        isOpen={showMonthlyLna} 
        onClose={() => setShowMonthlyLna(false)} 
        onComplete={() => fetchLnaAnalysis()} 
      />
    </Box>
  );
};

export default UserDashboard;
